import styled from '@emotion/styled';
import { Tooltip } from '@chakra-ui/react';
import { oldTheme } from '@resistapp/client/components/shared/old-styles';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import type { Filters } from '@resistapp/client/data-utils/filter-data/filter';
import { useIsModifyAsAdminActive } from '@resistapp/client/hooks/use-research-admin';
import { getKeyPressesFromMouseClick } from '@resistapp/client/utils/general';
import { L2Targets } from '@resistapp/common/assays';
import { friendlyL2Target } from '@resistapp/common/friendly';
import { SampleStatus, type Sample } from '@resistapp/common/types';
import { LegendOrdinal } from '@visx/legend';
import { ScaleOrdinal } from 'd3-scale';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { noTextSelection } from '../../shared/theme';
import { LegendStatus, type SampleStatusOrEmpty } from './legend-utils';

const gradientPadding = '20px';

interface Props {
  colorScale: ScaleOrdinal<string, string>;
  labelFormat: (s: string | number) => string;
  getOpacity: (s: string) => number;
  renderGlyph?: (args: { label: string; value: string; size: number }) => React.ReactNode;
  getTextStyle?: (s: string) => React.CSSProperties;
  getSamplesBySmplPntId?: (s: string | number) => Sample[] | undefined;
  onClick?: (id: string | number, keys: KeysPressOptions) => void;
  noColor?: boolean;
  height?: string;
  focusedSamplingPointIds?: number[];
  updateSampleStatuses?: (newStatus: SampleStatus, samplingPointId?: number) => void;
  filters?: Filters;
  /** Optional ref that will be attached to the first legend label text element. Useful for contextual onboarding tips. */
  firstLabelRef?: React.RefObject<HTMLElement>;
  /** Optional ref attached to the second legend label text element. */
  secondLabelRef?: React.RefObject<HTMLElement>;
}

// Utility to merge multiple refs into a single ref callback. Useful when we want to attach
// both firstLabelRef and secondLabelRef to the same DOM element in certain cases.
function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>): React.RefCallback<T> {
  return (value: T) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === 'function') {
        ref(value);
      } else {
        // Using `as` because we know it's a mutable ref object
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    }
  };
}

export function Legend({
  noColor,
  colorScale,
  labelFormat,
  getOpacity,
  renderGlyph,
  getTextStyle,
  onClick,
  height,
  getSamplesBySmplPntId,
  focusedSamplingPointIds,
  updateSampleStatuses,
  filters,
  firstLabelRef,
  secondLabelRef,
}: Props) {
  const [isModifyAsAdminActive] = useIsModifyAsAdminActive();
  const { allAssays } = useAssayContext();

  const statusBySmplPntId = useMemo(() => {
    if (!getSamplesBySmplPntId) return {};

    return focusedSamplingPointIds?.reduce<Record<number, SampleStatusOrEmpty>>((acc, envId) => {
      const samples = getSamplesBySmplPntId(envId);

      if (!samples) return acc;

      // Get unique statuses for this sampling point's samples
      const uniqueStatuses = [...new Set(samples.map(s => s.status))];

      // If all samples have the same status, use that status
      // Otherwise mark as empty string (which we use to indicate mixed/unknown status)
      acc[envId] = uniqueStatuses.length === 1 ? uniqueStatuses[0] : ' ';
      return acc;
    }, {});
  }, [getSamplesBySmplPntId, focusedSamplingPointIds]);

  const legendRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    const checkScrollable = () => {
      const element = legendRef.current;
      if (element) {
        setIsScrollable(element.scrollHeight > element.clientHeight);
      }
    };

    const handleScroll = () => {
      const element = legendRef.current;
      if (element) {
        setIsScrollable(
          element.scrollHeight > element.clientHeight &&
            element.scrollTop + element.clientHeight < element.scrollHeight,
        );
      }
    };

    const legendElement = legendRef.current;
    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    legendElement?.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', checkScrollable);
      legendElement?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const legendGlyphSize = 14;

  const changeStatus = (label: string, newStatus: SampleStatus) => {
    updateSampleStatuses?.(newStatus, Number(label));
  };

  return (
    <>
      <LegendOrdinal scale={colorScale} labelFormat={labelFormat}>
        {labels => {
          // Find first label considered "unselected" based on opacity < 1
          const firstUnselectedIndex = labels.findIndex(l => getOpacity(l.datum) < 1);

          const firstIndexForTip = firstUnselectedIndex >= 0 ? firstUnselectedIndex : 0;
          // For the second tip, anchor to the first unselected sample if any unselected, otherwise use index 1 (previous default)
          const secondIndexForTip = firstUnselectedIndex >= 0 ? firstUnselectedIndex : 1;

          return (
            <div
              ref={legendRef}
              style={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                marginLeft: '-30px',
                paddingLeft: '30px',
                height,
              }}
              className="download_legend-labels"
            >
              {labels.map((label, i) => {
                const opacity = getOpacity(label.datum);
                const textStyle = getTextStyle?.(label.text);

                // Determine if this label should get the contextual tip refs
                const attachFirst = i === firstIndexForTip && firstLabelRef;
                const attachSecond = i === secondIndexForTip && secondLabelRef;

                // Merge refs if both need to be attached to the same element
                const combinedRef =
                  attachFirst || attachSecond
                    ? mergeRefs(attachFirst ? firstLabelRef : undefined, attachSecond ? secondLabelRef : undefined)
                    : undefined;

                const displayLabel =
                  filters?.selectedTargetGrouping === 'assay'
                    ? allAssays.find(a => a.assay === label.text)?.gene ?? label.text
                    : label.text in L2Targets
                      ? friendlyL2Target(label.text as L2Targets)
                      : label.text;
                const glyphColor = label.value ?? 'transparent';

                return (
                  <LegendLabel
                    key={`sample-legend-quantile-${i}`}
                    onClick={e => {
                      if (onClick) {
                        onClick(label.datum, getKeyPressesFromMouseClick(e));
                      }
                    }}
                    style={{ opacity, cursor: onClick ? 'pointer' : undefined, ...textStyle }}
                  >
                    {isModifyAsAdminActive && label.datum && getSamplesBySmplPntId && (
                      <LegendStatus
                        status={statusBySmplPntId?.[Number(label.datum)]}
                        onStatusChange={newStatus => {
                          newStatus !== ' ' && changeStatus(label.datum, newStatus);
                        }}
                        isDisabled={!focusedSamplingPointIds?.includes(Number(label.datum))}
                      />
                    )}
                    {!noColor && (
                      <LegendSvg width={legendGlyphSize} height={legendGlyphSize} style={{ borderRadius: '2px' }}>
                        {renderGlyph ? (
                          renderGlyph({ label: String(label.text), value: glyphColor, size: legendGlyphSize })
                        ) : (
                          <rect fill={glyphColor} width={legendGlyphSize} height={legendGlyphSize} />
                        )}
                      </LegendSvg>
                    )}
                    <Tooltip label={displayLabel} openDelay={300} placement="auto" hasArrow>
                      <LegendText ref={combinedRef}>{displayLabel}</LegendText>
                    </Tooltip>
                  </LegendLabel>
                );
              })}
            </div>
          );
        }}
      </LegendOrdinal>
      {isScrollable && <BottomGradientContainer className="download_legend-labels-bottom-gradient" />}
    </>
  );
}

const BottomGradientContainer = styled.div`
  position: relative;
  height: ${gradientPadding};
  top: -${gradientPadding};
  background-image: linear-gradient(180deg, rgba(100, 100, 100, 0), white);
  pointer-events: none;
`;

export const LegendSvg = styled.svg`
  margin-top: 2px;
  margin-bottom: 2px;
  margin-right: ${oldTheme.spacing.xxs};
`;

export const LegendLabel = styled.div`
  height: 18px;
  width: 100%;
  white-space: nowrap;
  display: flex;
  padding-left: 5px;
  padding-right: 5px;
`;

export const LegendText = styled.span`
  ${noTextSelection}
  line-height: 18px;
  width: 100%;
  display: inline-block;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export type KeysPressOptions = {
  ctrl: boolean;
  shift: boolean;
};
